import { create } from 'zustand';
import type {
  Task,
  TaskType,
  CreateTaskRequest,
  UpdateTaskRequest,
  TaskOrderUpdate,
  TaskStatus,
  TaskPriority,
  GroupedTasks,
  TeamMember
} from '../types';
import { taskService } from '../features/tasks/services/taskService';
import { teamService } from '../features/teams/services/teamService';
import { taskTypeService } from '../features/task-types/services/taskTypeService';
import { useGoalStore } from './goalStore';
import { useWorkspaceStore } from './workspaceStore';

interface TaskStore {
  // 数据状态
  tasks: Task[];
  taskTypes: TaskType[];
  teamMembers: TeamMember[];
  currentTeamId: string | null;

  // 追踪正在处理的操作，避免WebSocket重复处理
  pendingOperations: {
    createdTaskIds: Set<string>;
    updatedTaskIds: Set<string>;
    deletedTaskIds: Set<string>;
  };

  // UI状态
  isCompactView: boolean;
  selectedTaskTypeId: string | null;
  selectedGoalId: string | null;
  selectedMemberIds: {
    IN_PROGRESS: string | null;
    DONE: string | null;
  };
  loading: {
    tasks: boolean;
    taskTypes: boolean;
    teamMembers: boolean;
    create: boolean;
    update: boolean;
    delete: boolean;
  };
  error: string | null;
  
  // 异步操作
  loadTasks: (workspace?: 'personal' | 'team', teamId?: string) => Promise<void>;
  loadTaskTypes: (workspace?: 'personal' | 'team', teamId?: string) => Promise<void>;
  loadTeamMembers: (teamId: string) => Promise<void>;
  clearTeamMembers: () => void;
  createTask: (task: CreateTaskRequest) => Promise<void>;
  updateTask: (id: string, updates: UpdateTaskRequest) => Promise<void>;
  deleteTask: (id: string) => Promise<void>;
  reorderTasks: (updates: TaskOrderUpdate[]) => Promise<void>;
  
  // UI操作
  toggleCompactView: () => void;
  setSelectedTaskTypeId: (typeId: string | null) => void;
  setSelectedGoalId: (goalId: string | null) => void;
  setSelectedMemberId: (status: 'IN_PROGRESS' | 'DONE', memberId: string | null) => void;
  clearError: () => void;
  
  // WebSocket同步操作
  syncTaskCreated: (task: Task) => void;
  syncTaskUpdated: (task: Task) => void;
  syncTaskDeleted: (taskId: string) => void;
  syncTasksReordered: (taskIds: string[]) => void;

  // 计算属性
  getTasksByStatus: (status: TaskStatus) => Task[];
  getTasksByPriority: (priority: TaskPriority) => Task[];
  getFilteredTasksByStatus: (status: TaskStatus) => Task[];
  getFilteredTasksByStatusAndMember: (status: TaskStatus) => Task[];
  getCompletedTasksByTime: () => GroupedTasks;
  getTaskCounts: () => { todo: number; inProgress: number; done: number };
}

export const useTaskStore = create<TaskStore>((set, get) => ({
  // 初始状态
  tasks: [],
  taskTypes: [],
  teamMembers: [],
  currentTeamId: null,
  pendingOperations: {
    createdTaskIds: new Set<string>(),
    updatedTaskIds: new Set<string>(),
    deletedTaskIds: new Set<string>(),
  },
  isCompactView: localStorage.getItem('compactView') === 'true',
  selectedTaskTypeId: null,
  selectedGoalId: null,
  selectedMemberIds: {
    IN_PROGRESS: null,
    DONE: null,
  },
  loading: {
    tasks: false,
    taskTypes: false,
    teamMembers: false,
    create: false,
    update: false,
    delete: false,
  },
  error: null,

  // 加载任务（支持工作空间）
  loadTasks: async (workspace?: 'personal' | 'team', teamId?: string) => {
    set(state => ({ loading: { ...state.loading, tasks: true }, error: null }));

    const timeout = new Promise<never>((_, reject) => {
      setTimeout(() => reject(new Error('加载任务超时，请检查网络连接或刷新页面重试')), 15000);
    });

    try {
      // 如果没有指定工作空间参数，尝试从workspaceStore获取当前工作空间
      if (!workspace) {
        try {
          const { useWorkspaceStore } = await import('./workspaceStore');
          const currentWorkspace = useWorkspaceStore.getState().currentWorkspace;
          workspace = currentWorkspace.type;
          if (currentWorkspace.type === 'team') {
            teamId = currentWorkspace.teamId;
          }
        } catch (error) {
          console.warn('无法获取工作空间状态，使用默认参数');
        }
      }

      const tasks = await Promise.race([
        taskService.getTasks(workspace, teamId),
        timeout
      ]);

      set(state => ({
        tasks,
        loading: { ...state.loading, tasks: false }
      }));
    } catch (error: any) {
      console.error('taskStore: 任务加载失败', error);
      set(state => ({
        loading: { ...state.loading, tasks: false },
        error: error.message || '加载任务失败'
      }));
    }
  },

  // 加载任务类型（支持工作空间）
  loadTaskTypes: async (workspace?: 'personal' | 'team', teamId?: string) => {
    set(state => ({ loading: { ...state.loading, taskTypes: true }, error: null }));

    const timeout = new Promise<never>((_, reject) => {
      setTimeout(() => reject(new Error('加载任务类型超时')), 10000);
    });

    try {
      // 如果没有指定工作空间参数，尝试从workspaceStore获取当前工作空间
      if (!workspace) {
        try {
          const { useWorkspaceStore } = await import('./workspaceStore');
          const currentWorkspace = useWorkspaceStore.getState().currentWorkspace;
          workspace = currentWorkspace.type;
          if (currentWorkspace.type === 'team') {
            teamId = currentWorkspace.teamId;
          }
        } catch (error) {
          console.warn('无法获取工作空间信息，使用默认方式加载');
        }
      }

      const taskTypes = await Promise.race([
        taskTypeService.getTaskTypes(workspace, teamId),
        timeout
      ]);

      set(state => ({
        taskTypes,
        loading: { ...state.loading, taskTypes: false }
      }));
    } catch (error: any) {
      console.error('💥 taskStore: 任务类型加载失败，设置loading=false', error);
      set(state => ({
        loading: { ...state.loading, taskTypes: false },
        error: error.message || '加载任务类型失败'
      }));
    }
  },

  // 加载团队成员
  loadTeamMembers: async (teamId: string) => {
    const { loading, teamMembers, currentTeamId } = get();
    
    // 防止重复请求：如果正在加载或已经是同一个团队的数据，则跳过
    if (loading.teamMembers || (currentTeamId === teamId && teamMembers.length > 0)) return;
    
    set(state => ({ 
      loading: { ...state.loading, teamMembers: true }, 
      error: null,
      currentTeamId: teamId
    }));
    
    try {
      const teamMembers = await teamService.getTeamMembers(teamId);
      set(state => ({ 
        teamMembers, 
        loading: { ...state.loading, teamMembers: false }
      }));
    } catch (error: any) {
      set(state => ({ 
        loading: { ...state.loading, teamMembers: false },
        error: error.message || '加载团队成员失败'
      }));
    }
  },

  // 清理团队成员数据（切换到个人空间时使用）
  clearTeamMembers: () => {
    set(state => ({
      teamMembers: [],
      currentTeamId: null
    }));
  },

  // 创建任务
  createTask: async (taskData: CreateTaskRequest) => {
    set(state => ({ loading: { ...state.loading, create: true }, error: null }));

    // 生成临时ID用于标记pending状态（实际的ID由服务器生成）
    const tempId = `temp_${Date.now()}_${Math.random()}`;
    get().pendingOperations.createdTaskIds.add(tempId);

    try {
      // 获取当前工作空间信息
      let workspace: 'personal' | 'team' = 'personal';
      let teamId: string | undefined;

      try {
        const { useWorkspaceStore } = await import('./workspaceStore');
        const currentWorkspace = useWorkspaceStore.getState().currentWorkspace;
        workspace = currentWorkspace.type;
        if (currentWorkspace.type === 'team') {
          teamId = currentWorkspace.teamId;
        }
      } catch (error) {
        console.warn('无法获取工作空间状态，使用默认参数');
      }

      const newTask = await taskService.createTask(taskData, workspace, teamId);
      console.log('createTask: API返回新任务', newTask.id);

      // 替换临时ID为真实ID
      get().pendingOperations.createdTaskIds.delete(tempId);
      get().pendingOperations.createdTaskIds.add(newTask.id);

      // 检查WebSocket是否已经添加了这个任务
      const alreadyAdded = get().tasks.some(t => t.id === newTask.id);
      if (!alreadyAdded) {
        console.log('createTask: WebSocket未添加，手动添加任务', newTask.id);
        set(state => ({
          tasks: [...state.tasks, newTask],
          loading: { ...state.loading, create: false }
        }));
      } else {
        console.log('createTask: WebSocket已添加任务，跳过', newTask.id);
        set(state => ({
          loading: { ...state.loading, create: false }
        }));
      }

      // 5秒后从pending集合中移除
      setTimeout(() => {
        get().pendingOperations.createdTaskIds.delete(newTask.id);
      }, 5000);
    } catch (error: any) {
      get().pendingOperations.createdTaskIds.delete(tempId);
      set(state => ({
        loading: { ...state.loading, create: false },
        error: error.message || '创建任务失败'
      }));
      throw error;
    }
  },

  // 更新任务（乐观更新）
  updateTask: async (id: string, updates: UpdateTaskRequest) => {
    // 1. 乐观更新UI
    const oldTasks = get().tasks;
    const optimisticTasks = oldTasks.map(task => 
      task.id === id ? { ...task, ...updates, updatedAt: new Date().toISOString() } : task
    );
    
    set(state => ({ 
      tasks: optimisticTasks,
      loading: { ...state.loading, update: true },
      error: null 
    }));

    try {
      // 2. 发送API请求
      const updatedTask = await taskService.updateTask(id, updates);

      // 将任务ID加入pending集合，防止WebSocket重复处理
      get().pendingOperations.updatedTaskIds.add(id);

      // 3. 用服务器响应更新
      set(state => ({
        tasks: state.tasks.map(task => task.id === id ? updatedTask : task),
        loading: { ...state.loading, update: false }
      }));

      // 3秒后从pending集合中移除
      setTimeout(() => {
        get().pendingOperations.updatedTaskIds.delete(id);
      }, 3000);

      // 4. 如果任务状态发生变化并且有关联目标，刷新目标进度
      if (updates.status && updatedTask.goalId) {
        const goalStore = useGoalStore.getState();

        // 只刷新特定目标的进度，而不是整个目标列表
        goalStore.refreshGoalProgress(updatedTask.goalId);

        // 5. 检查目标状态变化
        if (updates.status === 'DONE') {
          // 任务完成时，检查目标是否应该完成
          setTimeout(() => {
            const currentTasks = get().tasks;
            const shouldComplete = goalStore.checkGoalCompletion(updatedTask.goalId, currentTasks);

            if (shouldComplete) {
              goalStore.promptGoalCompletion(updatedTask.goalId);
            }
          }, 100); // 短暂延迟确保数据更新完成
        } else if (updates.status && updates.status !== 'DONE') {
          // 任务状态从DONE变为其他状态时，检查目标是否需要重新激活
          setTimeout(() => {
            const currentTasks = get().tasks;
            const shouldReactivate = goalStore.checkGoalReactivation(updatedTask.goalId, currentTasks);

            if (shouldReactivate) {
              goalStore.reactivateGoal(updatedTask.goalId);
            }
          }, 100);
        }
      }
    } catch (error: any) {
      // 4. 出错时回滚
      set(state => ({ 
        tasks: oldTasks,
        loading: { ...state.loading, update: false },
        error: error.message || '更新任务失败'
      }));
      throw error;
    }
  },

  // 删除任务
  deleteTask: async (id: string) => {
    const oldTasks = get().tasks;
    
    // 乐观删除
    set(state => ({ 
      tasks: state.tasks.filter(task => task.id !== id),
      loading: { ...state.loading, delete: true },
      error: null 
    }));

    try {
      await taskService.deleteTask(id);

      // 将任务ID加入pending集合，防止WebSocket重复处理
      get().pendingOperations.deletedTaskIds.add(id);

      set(state => ({ loading: { ...state.loading, delete: false } }));

      // 3秒后从pending集合中移除
      setTimeout(() => {
        get().pendingOperations.deletedTaskIds.delete(id);
      }, 3000);
    } catch (error: any) {
      // 出错时恢复
      set(state => ({ 
        tasks: oldTasks,
        loading: { ...state.loading, delete: false },
        error: error.message || '删除任务失败'
      }));
      throw error;
    }
  },

  // 重新排序任务
  reorderTasks: async (updates: TaskOrderUpdate[]) => {
    const oldTasks = get().tasks;
    
    // 乐观更新
    let optimisticTasks = [...oldTasks];
    updates.forEach(update => {
      const task = optimisticTasks.find(t => t.id === update.taskId);
      if (task) {
        task.status = update.newStatus;
        task.priority = update.newPriority;
        task.order = update.newOrder;
        task.updatedAt = new Date().toISOString();
      }
    });
    
    set({ tasks: optimisticTasks, error: null });

    try {
      await taskService.reorderTasks(updates);

      // 检查目标状态变化
      const goalStore = useGoalStore.getState();
      const affectedGoalIds = new Set<string>();

      updates.forEach(update => {
        const task = optimisticTasks.find(t => t.id === update.taskId);
        if (task?.goalId) {
          affectedGoalIds.add(task.goalId);
        }
      });

      // 检查每个受影响的目标
      affectedGoalIds.forEach(goalId => {
        setTimeout(() => {
          const currentTasks = get().tasks;

          // 检查是否有任务变为DONE状态，需要提示完成目标
          const hasNewlyCompletedTasks = updates.some(update => {
            const task = optimisticTasks.find(t => t.id === update.taskId);
            return update.newStatus === 'DONE' && task?.goalId === goalId;
          });

          if (hasNewlyCompletedTasks) {
            const shouldComplete = goalStore.checkGoalCompletion(goalId, currentTasks);
            if (shouldComplete) {
              goalStore.promptGoalCompletion(goalId);
            }
          }

          // 检查是否有任务从DONE变为其他状态，需要重新激活目标
          const hasTasksMovedFromDone = updates.some(update => {
            const task = optimisticTasks.find(t => t.id === update.taskId);
            return update.newStatus !== 'DONE' && task?.goalId === goalId;
          });

          if (hasTasksMovedFromDone) {
            const shouldReactivate = goalStore.checkGoalReactivation(goalId, currentTasks);
            if (shouldReactivate) {
              goalStore.reactivateGoal(goalId);
            }
          }
        }, 100);
      });
    } catch (error: any) {
      // 出错时回滚
      set(state => ({ 
        tasks: oldTasks,
        error: error.message || '重新排序失败'
      }));
      throw error;
    }
  },

  // 切换紧凑视图
  toggleCompactView: () => {
    const newCompactView = !get().isCompactView;
    localStorage.setItem('compactView', String(newCompactView));
    set({ isCompactView: newCompactView });
  },

  // 设置选中的任务类型ID
  setSelectedTaskTypeId: (typeId: string | null) => {
    set({ selectedTaskTypeId: typeId });
  },
  // 设置选中的目标ID
  setSelectedGoalId: (goalId: string | null) => {
    set({ selectedGoalId: goalId });
  },

  // 设置选中的团队成员ID
  setSelectedMemberId: (status: 'IN_PROGRESS' | 'DONE', memberId: string | null) => {
    set(state => ({
      selectedMemberIds: {
        ...state.selectedMemberIds,
        [status]: memberId
      }
    }));
  },

  // 清除错误
  clearError: () => {
    set({ error: null });
  },

  // 按状态获取任务
  getTasksByStatus: (status: TaskStatus) => {
    return get().tasks
      .filter(task => task.status === status)
      .sort((a, b) => a.order - b.order);
  },

  // 按优先级获取任务
  getTasksByPriority: (priority: TaskPriority) => {
    return get().tasks
      .filter(task => task.priority === priority)
      .sort((a, b) => a.order - b.order);
  },

  // 按状态和任务类型获取过滤后的任务
  getFilteredTasksByStatus: (status: TaskStatus) => {
    const { tasks, selectedTaskTypeId, selectedGoalId } = get();
    let filteredTasks = tasks.filter(task => task.status === status);

    // 如果选中了特定的目标，进行过滤
    if (selectedGoalId) {
      if (selectedGoalId === 'unlinked') {
        // 筛选无关联目标的任务
        filteredTasks = filteredTasks.filter(task => !task.goalId);
      } else {
        // 筛选特定目标的任务，但需要检查目标状态
        const goalStore = useGoalStore.getState();
        const selectedGoal = goalStore.goals.find(goal => goal.id === selectedGoalId);

        if (selectedGoal) {
          // 只有当目标状态为ACTIVE或COMPLETED时才显示任务
          // PAUSED状态的目标不应该显示任务
          if (selectedGoal.status === 'ACTIVE' || selectedGoal.status === 'COMPLETED') {
            filteredTasks = filteredTasks.filter(task => task.goalId === selectedGoalId);
          } else {
            // 暂停或取消的目标不显示任务
            filteredTasks = [];
          }
        } else {
          // 如果找不到目标，不显示任务
          filteredTasks = [];
        }
      }
    } else {
      // 没有选择特定目标时，只显示激活目标的任务或无关联目标的任务
      filteredTasks = filteredTasks.filter(task => {
        // 如果任务没有关联目标，显示
        if (!task.goalId) return true;

        // 如果任务有关联目标，只有当目标状态为ACTIVE时才显示
        if (task.goal && task.goal.status === 'ACTIVE') return true;

        return false;
      });
    }

    // 如果选中了特定的任务类型，进行过滤
    if (selectedTaskTypeId) {
      filteredTasks = filteredTasks.filter(task => task.taskTypeId === selectedTaskTypeId);
    }

    return filteredTasks.sort((a, b) => a.order - b.order);
  },

  // 按状态和团队成员获取过滤后的任务
  getFilteredTasksByStatusAndMember: (status: TaskStatus) => {
    const { tasks, selectedTaskTypeId, selectedGoalId, selectedMemberIds } = get();
    let filteredTasks = tasks.filter(task => task.status === status);

    // 如果选中了特定的目标，进行过滤
    if (selectedGoalId) {
      if (selectedGoalId === 'unlinked') {
        // 筛选无关联目标的任务
        filteredTasks = filteredTasks.filter(task => !task.goalId);
      } else {
        // 筛选特定目标的任务，但需要检查目标状态
        const goalStore = useGoalStore.getState();
        const selectedGoal = goalStore.goals.find(goal => goal.id === selectedGoalId);

        if (selectedGoal) {
          // 只有当目标状态为ACTIVE或COMPLETED时才显示任务
          // PAUSED状态的目标不应该显示任务
          if (selectedGoal.status === 'ACTIVE' || selectedGoal.status === 'COMPLETED') {
            filteredTasks = filteredTasks.filter(task => task.goalId === selectedGoalId);
          } else {
            // 暂停或取消的目标不显示任务
            filteredTasks = [];
          }
        } else {
          // 如果找不到目标，不显示任务
          filteredTasks = [];
        }
      }
    } else {
      // 没有选择特定目标时，只显示激活目标的任务或无关联目标的任务
      filteredTasks = filteredTasks.filter(task => {
        // 如果任务没有关联目标，显示
        if (!task.goalId) return true;

        // 如果任务有关联目标，只有当目标状态为ACTIVE时才显示
        if (task.goal && task.goal.status === 'ACTIVE') return true;

        return false;
      });
    }

    // 如果选中了特定的任务类型，进行过滤
    if (selectedTaskTypeId) {
      filteredTasks = filteredTasks.filter(task => task.taskTypeId === selectedTaskTypeId);
    }

    // 如果选中了特定的团队成员，进行过滤 (只对IN_PROGRESS和DONE状态有效)
    if ((status === 'IN_PROGRESS' || status === 'DONE') && selectedMemberIds[status]) {
      filteredTasks = filteredTasks.filter(task => task.user?.id === selectedMemberIds[status]);
    }

    return filteredTasks.sort((a, b) => a.order - b.order);
  },

  // 按时间分组获取已完成任务
  getCompletedTasksByTime: (): GroupedTasks => {
    const completedTasks = get().tasks.filter(task => task.status === 'DONE');
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);
    const thisWeekStart = new Date(today);
    thisWeekStart.setDate(today.getDate() - today.getDay());

    const grouped: GroupedTasks = {
      today: [],
      yesterday: [],
      thisweek: []
    };

    completedTasks.forEach(task => {
      if (!task.taskDate) return;
      
      const taskDate = new Date(task.taskDate);
      const taskDay = new Date(taskDate.getFullYear(), taskDate.getMonth(), taskDate.getDate());
      
      if (taskDay.getTime() === today.getTime()) {
        grouped.today.push(task);
      } else if (taskDay.getTime() === yesterday.getTime()) {
        grouped.yesterday.push(task);
      } else if (taskDay >= thisWeekStart) {
        grouped.thisweek.push(task);
      }
    });

    return grouped;
  },

  // 获取任务计数
  getTaskCounts: () => {
    const tasks = get().tasks;
    return {
      todo: tasks.filter(t => t.status === 'TODO').length,
      inProgress: tasks.filter(t => t.status === 'IN_PROGRESS').length,
      done: tasks.filter(t => t.status === 'DONE').length,
    };
  },

  // WebSocket同步：任务创建
  syncTaskCreated: (task: Task) => {
    // 第一检查：任务是否已存在
    const exists = get().tasks.some(t => t.id === task.id);
    if (exists) {
      console.log('syncTaskCreated: 任务已存在，跳过', task.id);
      return;
    }

    // 第二检查：任务是否在pending中（说明是当前客户端创建的，正在等待API响应）
    if (get().pendingOperations.createdTaskIds.has(task.id)) {
      console.log('syncTaskCreated: 任务在pending中，等待API响应', task.id);
      // 延迟200ms后再次检查，如果API响应慢于WebSocket，则添加任务
      setTimeout(() => {
        const stillNotExists = !get().tasks.some(t => t.id === task.id);
        if (stillNotExists) {
          console.log('syncTaskCreated: API响应未添加任务，WebSocket补充添加', task.id);
          set(state => ({
            tasks: [...state.tasks, task]
          }));
        }
      }, 200);
      return;
    }

    // 如果任务不存在且不在pending中，立即添加到state
    console.log('syncTaskCreated: 添加新任务', task.id);
    set(state => ({
      tasks: [...state.tasks, task]
    }));
  },

  // WebSocket同步：任务更新
  syncTaskUpdated: (updatedTask: Task) => {
    // 如果任务ID在pending集合中，说明是当前浏览器更新的，跳过
    if (get().pendingOperations.updatedTaskIds.has(updatedTask.id)) {
      return;
    }

    set(state => {
      const taskIndex = state.tasks.findIndex(t => t.id === updatedTask.id);

      if (taskIndex === -1) {
        return state;
      }

      const localTask = state.tasks[taskIndex];

      // 比较时间戳，只有远程任务更新时间晚于本地时才更新
      if (localTask.updatedAt && updatedTask.updatedAt) {
        const localTime = new Date(localTask.updatedAt).getTime();
        const remoteTime = new Date(updatedTask.updatedAt).getTime();

        if (remoteTime <= localTime) {
          return state;
        }
      }

      const newTasks = [...state.tasks];
      newTasks[taskIndex] = updatedTask;

      return { tasks: newTasks };
    });
  },

  // WebSocket同步：任务删除
  syncTaskDeleted: (taskId: string) => {
    // 如果任务ID在pending集合中，说明是当前浏览器删除的，跳过
    if (get().pendingOperations.deletedTaskIds.has(taskId)) {
      return;
    }

    set(state => {
      const exists = state.tasks.some(t => t.id === taskId);

      if (!exists) {
        return state;
      }

      return {
        tasks: state.tasks.filter(t => t.id !== taskId)
      };
    });
  },

  // WebSocket同步：任务重排序
  syncTasksReordered: (taskIds: string[]) => {
    // 重排序事件：只刷新受影响的任务，而不是重新加载所有任务
    console.log('syncTasksReordered: 收到重排序事件，影响任务数:', taskIds.length);

    // 对于重排序事件，我们不需要做任何事情
    // 因为拖拽操作本身已经在本地更新了order
    // WebSocket事件只是确认服务器已经保存了这些更改

    // 如果需要确保数据一致性，可以只重新获取这些特定任务的order值
    // 但对于性能考虑，我们信任本地的乐观更新
  }
}));